import React, {useEffect} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {groupBy, map, filter, sortBy, get} from 'lodash';
import {
    Cookie,
    SetLibraryMeasureAction
} from "mindsets-js-sdk";

import ActiveChallenge from './ActiveChallenge';
import ChallengesFilter from './ChallengesFilter';
import NoChallenges from './NoChallenges';
import {MEASURE_COOKIE_NAME} from 'constants/index';
import {sortCategories, getCategoryText, getSchoolCurriculumId} from "common/challengeNew/utils";
import useLibraryChallenges from "../../../hooks/useLibraryChallenges";

export default function Challenges() {
    const dispatch = useDispatch();
    const me = useSelector(state => state.me);
    const challenge_versions = useSelector(state => state.challenge_versions);
    const subject = useSelector(state => state.library_subject);
    const grade = useSelector(state => state.library_grade);
    const theme = useSelector(state => state.library_theme);
    const topic = useSelector(state => state.library_topic);
    const measure = useSelector(state => state.library_measure);
    const {loading, reload} = useLibraryChallenges();

    function setMeasure() {
        // UK Schools are metric only and the filter is not shown to them. Erase
        // the cookie so a measure carried over from another School cannot
        // conflict with that.
        if ('GB' === get(me, 'school.country')) {
            Cookie.eraseCookie(MEASURE_COOKIE_NAME);
            if ('metric' !== measure) {
                dispatch(SetLibraryMeasureAction('metric'));
            }

            return;
        }
        let measure_new = 'metric';
        const cookie_measure = Cookie.getCookie(MEASURE_COOKIE_NAME);
        if (cookie_measure) {
            measure_new = cookie_measure;
        } else if ('US' === get(me, 'school.country')) {
            measure_new = 'imperial';
        }
        if (measure_new !== measure) {
            dispatch(SetLibraryMeasureAction(measure_new));
        }
    }

    function filterChallenges() {
        const school_curriculum_id = getSchoolCurriculumId(me);

        return filter(challenge_versions, (challenge_version) => {
            if ('active' !== challenge_version.challenge_code.status) {
                return false;
            }
            if (challenge_version.challenge_code.subject !== subject) {
                return false;
            }
            if (topic && '' !== topic) {
                if (challenge_version.challenge_code.standards
                    .filter(standard => (standard.curriculum_id === school_curriculum_id) && standard.topic === topic)
                    .length <= 0) {
                    return false;
                }
            }
            if (grade && '' !== grade) {
                if (challenge_version.challenge_code.standards
                    .filter(standard => (standard.curriculum_id === school_curriculum_id) && standard.grade === grade)
                    .length <= 0) {
                    return false;
                }
            }
            if (theme && '' !== theme) {
                if (challenge_version.challenge_code.themes.indexOf(theme) < 0) {
                    return false;
                }
            }
            if (measure && '' !== measure) {
                if (challenge_version.challenge_code.measure && challenge_version.challenge_code.measure !== measure) {
                    return false;
                }
            }

            return true;
        });
    }

    function renderLoader() {
        if (loading) {
            return (
                <div className="section">
                    <div className="progress">
                        <div className="indeterminate"/>
                    </div>
                </div>
            );
        }
    }

    function renderChallenges() {
        if (!me) {
            return null;
        }
        const filtered_challenge_versions = filterChallenges();
        if (filtered_challenge_versions.length <= 0 && false === loading) {
            // The unfiltered list. NoChallenges suggests the latest Challenges
            // available to this user, so it picks from everything rather than
            // from what the filters just excluded.
            return (
                <NoChallenges
                    challenge_versions={challenge_versions}
                />
            );
        }
        const grade_translated_challenges = map(filtered_challenge_versions, (c) => {
            const challenge_version = JSON.parse(JSON.stringify(c));
            const grade = me.curriculum && challenge_version.challenge_code.grades[me.curriculum.code] ? challenge_version.challenge_code.grades[me.curriculum.code] : challenge_version.challenge_code.grades.default
            challenge_version.grade_translated = getCategoryText(grade);

            return challenge_version;
        })
        const ordered_challenges = sortBy(grade_translated_challenges, (c) => c.name)
        const categorised_challenges = groupBy(ordered_challenges, (c) => c.grade_translated)

        return map(sortCategories(categorised_challenges), (category, grade) => {
            return (
                <div className="section" key={grade}>
                    <h4 className="header-new">
                        {grade}
                    </h4>
                    <div className="row">
                        {
                            category.map((challenge_version, k) => {
                                return (
                                    <div className="col" key={k}>
                                        <ActiveChallenge challenge_version={challenge_version}/>
                                    </div>
                                );
                            })
                        }
                    </div>
                    <div className="divider"/>
                </div>
            );
        });
    }

    // Loading the Challenges, and resetting filters left over from another
    // curriculum, is handled by useLibraryChallenges.
    useEffect(() => {
        if (me) {
            setMeasure();
            const userChannel = pusher.subscribe('user_' + me.id);
            userChannel.bind('user.updated', reload);

            return () => {
                // Only our own binding. This channel is shared with App.js, and
                // an argumentless unbind() drops every callback on it, while
                // unsubscribe() stops the server sending on it at all.
                userChannel.unbind('user.updated', reload);
            }
        }
    }, [me]);

    return (
        <div>
            {renderLoader()}

            <div className="section">
                <ChallengesFilter/>
            </div>

            {renderChallenges()}
        </div>
    );
}
